// Ambient Layer — BootEye
// One-shot boot intro: a giant photoreal eye opens (lid wipe + iris bloom),
// then the black scrim fades. sessionStorage-gated to play once per session.
import * as React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import bootEyeImg from "../../../images/friendlyeye1.png";
import './booteye.css';

function BootEye(props) {
    const onDone = props.onDone;
    const [show, setShow] = useState(() => {
        try {
            return !sessionStorage.getItem("ambulate_booted");
        } catch (e) {
            return false;
        }
    });

    // Notify the parent (App, which gates the music dock) exactly once when the
    // boot intro is finished — and only then, so nothing pops in over the load
    // animation.
    const doneRef = useRef(false);
    const finish = useCallback(() => {
        if (doneRef.current) return;
        doneRef.current = true;
        try {
            sessionStorage.setItem("ambulate_booted", "1");
        } catch (e) { /* private mode — fail silent */ }
        setShow(false);
        if (typeof onDone === "function") onDone();
    }, [onDone]);

    useEffect(() => {
        // Already booted this session → there is no intro to wait for, so the
        // dock can come up as soon as the page itself has loaded.
        if (!show) {
            if (!doneRef.current) {
                doneRef.current = true;
                if (typeof onDone === "function") onDone();
            }
            return undefined;
        }
        // One-shot: after the bootFade finishes, unmount the scrim entirely so it
        // is removed from the DOM (not left as a persistent full-screen z-index:50
        // layer painting over the page). The timeout is the source of truth; the
        // onAnimationEnd handler below is a belt-and-braces fallback.
        const t = setTimeout(finish, 2600);
        return () => clearTimeout(t);
    }, [show, finish, onDone]);

    // Belt-and-braces: if the fade animation ends (e.g. the timer was throttled
    // by a backgrounded tab), tear the scrim down immediately on animationend.
    const handleFadeEnd = (e) => {
        if (e && e.animationName && e.animationName.indexOf("bootFade") === -1) return;
        finish();
    };

    if (!show) return null;

    return (
        <div className="boot-eye-scrim" aria-hidden="true" onAnimationEnd={handleFadeEnd}>
            <div className="boot-eye">
                <img className="boot-eye-img" src={bootEyeImg} alt="" draggable="false" />
                <div className="boot-lid" />
            </div>
            <div className="boot-caption">welcome</div>
        </div>
    );
}

export default BootEye;
